"use client";
import React, { useState, useCallback } from "react";
import { SETTINGS_CONFIG } from "@/constants/workspace/constants";
import { Settings, Lock, Database, Zap, Globe, Cloud, ChevronRight } from "lucide-react";

// --- Types ---
type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH" | "OPTIONS";
type SettingValue = string | number | boolean | string[] | HttpMethod[] | Record<string, unknown>;

interface FieldConfig {
    id: string;
    type: string;
    label: string;
    path?: string[];
    inputType?: string;
    isArray?: boolean;
    defaultValue?: SettingValue;
    options?: string[];
    dependsOn?: string;
    fields?: FieldConfig[];
    groupLabel?: string;
    gridCol?: string;
    showWhen?: {
        path: string[];
        value: SettingValue;
    };
}

const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
    Settings: Settings,
    Lock: Lock,
    Database: Database,
    Zap: Zap,
    Globe: Globe,
    Cloud: Cloud,
};

// --- Mock Data ---
const INITIAL_DATA = {
    runtime: "Node.js 18",
    authentication: true,
    authenticationMethods: {
        emailPassword: true,
        googleAuth: false,
    },
    databaseType: "PostgreSQL",
    fileStorage: false,
    storageType: "AWS S3",
    rateLimiterEnabled: true,
    rateLimiterConfig: {
        windowMs: 900000,
        maxRequests: 100,
        standardHeaders: true,
        legacyHeaders: false,
        skipFailedRequests: false,
        message: "Too many requests, please try again later.",
    },
    corsEnabled: true,
    corsConfig: {
        allowedOrigins: ["https://myapp.com", "http://localhost:3000"],
        allowedHeaders: ["Content-Type", "Authorization"],
        allowedMethods: ["GET", "POST", "PUT", "DELETE", "OPTIONS"],
        allowCredentials: true,
    },
    autoScaleConfig: {
        enabled: false,
        cloudProvider: "AWS",
        scalingPolicy: "horizontal",
        minInstances: 1,
        maxInstances: 10,
        targetCPUUtilization: 70,
        targetMemoryUtilization: 80,
        cooldownPeriod: 300,
        awsRegion: "us-east-1",
    }
};

// --- Helper Components ---

const Toggle = ({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) => (
    <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`w-11 h-6 rounded-full relative transition-colors ${checked ? "bg-primary" : "bg-border"} ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
        <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${checked ? "left-6" : "left-1"}`} />
    </button>
);

const Dropdown = ({ value, options, onChange, disabled }: { value: string; options: string[]; onChange: (v: string) => void; disabled?: boolean }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="relative w-full">
            <button
                type="button"
                disabled={disabled}
                onClick={() => setIsOpen(!isOpen)}
                className={`h-10 w-full bg-background border border-border rounded-lg flex items-center px-4 text-sm text-primary-text justify-between shadow-sm hover:border-primary/20 transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
            >
                <span className="truncate">{value}</span>
                <ChevronRight className={`w-4 h-4 transition-transform opacity-50 ${isOpen ? "-rotate-90" : "rotate-90"}`} />
            </button>
            {isOpen && !disabled && (
                <div className="absolute top-full left-0 w-full mt-1 bg-surface border border-border rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
                    {options.map((opt) => (
                        <button
                            key={opt}
                            type="button"
                            onClick={() => { onChange(opt); setIsOpen(false); }}
                            className="w-full text-left px-4 py-2 text-sm text-primary-text hover:bg-primary/10 transition-colors"
                        >
                            {opt}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const MethodToggle = ({ value, options, onChange, disabled }: { value: HttpMethod[]; options: string[]; onChange: (v: HttpMethod[]) => void; disabled?: boolean }) => {
    const toggleMethod = (method: string) => {
        const m = method as HttpMethod;
        if (value.includes(m)) {
            onChange(value.filter(v => v !== m));
        } else {
            onChange([...value, m]);
        }
    };

    return (
        <div className="flex flex-wrap gap-2">
            {options.map(opt => (
                <button
                    key={opt}
                    type="button"
                    disabled={disabled}
                    onClick={() => toggleMethod(opt)}
                    className={`px-3 py-1 rounded-md text-xs font-medium border transition-colors ${value.includes(opt as HttpMethod)
                            ? "bg-primary/10 border-primary text-primary"
                            : "bg-background border-border text-secondary-text hover:border-primary/30"
                        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                    {opt}
                </button>
            ))}
        </div>
    );
};

// --- Main Component ---

export const InteractiveSettings = () => {
    const [activeSectionId, setActiveSectionId] = useState("general");
    const [data, setData] = useState(INITIAL_DATA);

    const activeSection = SETTINGS_CONFIG.sections.find(s => s.id === activeSectionId) || SETTINGS_CONFIG.sections[0];

    // --- Logic ---
    const getValueAtPath = useCallback((path?: string[]): SettingValue | undefined => {
        if (!path || path.length === 0) return undefined;
        let cur: Record<string, unknown> = data;
        for (const p of path) {
            if (cur === undefined || cur === null) return undefined;
            cur = cur[p] as Record<string, unknown>;
        }
        return cur as unknown as SettingValue;
    }, [data]);

    const handleSettingChange = useCallback((path: string[], value: SettingValue) => {
        setData((prev) => {
            const copy = JSON.parse(JSON.stringify(prev)) as typeof INITIAL_DATA; // Deep copy for simplicity
            let cur: Record<string, unknown> = copy;
            for (let i = 0; i < path.length - 1; i++) {
                const key = path[i];
                if (!cur[key]) cur[key] = {};
                cur = cur[key] as Record<string, unknown>;
            }
            cur[path[path.length - 1]] = value;
            return copy;
        });
    }, []);

    const shouldShowField = useCallback((field: FieldConfig): boolean => {
        if (!field.showWhen) return true;
        const conditionValue = getValueAtPath(field.showWhen.path);
        return conditionValue === field.showWhen.value;
    }, [getValueAtPath]);

    // --- Descriptions ---
    const SECTION_DESCRIPTIONS: Record<string, string> = {
        general: "Manage core application settings, environment variables, and project metadata.",
        auth: "Secure your application with built-in auth providers (Google, GitHub) and role-based access control.",
        storage: "Configure object storage buckets (S3, GCS) for user uploads, assets, and automated backups.",
        rateLimit: "Protect your API from abuse with configurable global and per-route rate limiting rules.",
        cors: "Manage Cross-Origin Resource Sharing policies to control which domains can access your API.",
        autoscaling: "Scale your application infrastructure automatically based on CPU and memory usage."
    };

    const FIELD_DESCRIPTIONS: Record<string, string> = {
        runtime: "Select the runtime version for your backend.",
        authentication: "Master switch to enable or disable all user authentication features.",
        emailPassword: "Allow users to sign up and log in using their email address and a password.",
        googleAuth: "Enable 'Sign in with Google' using OAuth 2.0.",
        primaryDatabase: "Choose the main database engine (PostgreSQL, MongoDB, etc.).",
        fileStorageToggle: "Enable file upload capabilities for your application.",
        fileStorageBucket: "Select the storage provider bucket for persisting files.",
        rateLimiterEnabled: "Enable global rate limiting to prevent DDoS and abuse.",
        windowMs: "Time window in milliseconds for the rate limit quota.",
        maxRequests: "Maximum number of requests allowed per IP within the window.",
        standardHeaders: "Return standard RateLimit headers (X-RateLimit-Limit, etc.).",
        legacyHeaders: "Return legacy X-Draft-RateLimit headers.",
        skipFailedRequests: "Do not count failed requests towards the rate limit quota.",
        message: "Custom JSON response sent when the rate limit is exceeded.",
        corsEnabled: "Enable CORS to allow requests from specific origins.",
        allowedOrigins: "List of domains allowed to access your API (e.g., https://myapp.com).",
        allowedHeaders: "HTTP headers that can be used during the actual request.",
        allowedMethods: "HTTP methods allowed when accessing the resource.",
        allowCredentials: "Allow cookies and authorization headers in cross-origin requests.",
        autoscalingEnabled: "Automatically adjust the number of instances based on load.",
        cloudProvider: "Select the cloud provider for deployment (AWS, GCP, Azure).",
        scalingPolicy: "Choose between horizontal (more instances) or vertical (larger instances) scaling.",
        minInstances: "Minimum number of instances to keep running.",
        maxInstances: "Maximum number of instances allowed during peak load.",
        targetCPU: "Average CPU utilization target to trigger scaling.",
        targetMemory: "Average memory utilization target to trigger scaling.",
        cooldownPeriod: "Wait time between scaling activities to allow stabilization.",
        awsRegion: "The AWS region where your resources will be provisioned.",
        awsAutoScalingGroupName: "Name of the existing Auto Scaling Group.",
        awsLoadBalancerArn: "ARN of the Load Balancer to attach instances to.",
        gcpProjectId: "Google Cloud Project ID.",
        gcpZone: "GCP Zone for deployment.",
        gcpServiceName: "Name of the Cloud Run service or GKE cluster.",
        azureResourceGroup: "Azure Resource Group containing your App Service.",
        azureLocation: "Azure region location.",
        azureAppServiceName: "Name of the Azure App Service."
    };

    // --- Renderers ---
    const renderField = (field: FieldConfig, disabled = false) => {
        if (!shouldShowField(field)) return null;

        if (field.type === "sectionTitle") {
            return (
                <h4 key={field.id} className="text-xs font-bold text-primary/80 uppercase tracking-widest mt-8 mb-4 flex items-center gap-2">
                    {field.label}
                    <div className="h-px flex-1 bg-border" />
                </h4>
            );
        }

        if (field.type === "conditionalGroup") {
            const dependsPath = field.dependsOn?.includes('.') ? field.dependsOn.split('.') : (field.dependsOn ? [field.dependsOn] : undefined);
            const dependsValue = dependsPath ? getValueAtPath(dependsPath) : true;
            const groupDisabled = !dependsValue || disabled;

            // Check if any sub-fields are visible
            const visibleFields = field.fields?.filter(sub => shouldShowField(sub)) || [];
            if (visibleFields.length === 0) return null;

            return (
                <div key={field.id} className={`pl-4 border-l-2 ${groupDisabled ? "border-border" : "border-primary/30"} space-y-4 transition-colors duration-300`}>
                    {field.groupLabel && <h5 className="text-sm font-semibold text-primary-text">{field.groupLabel}</h5>}
                    <div className="grid grid-cols-1 gap-4">
                        {visibleFields.map(sub => renderField(sub, groupDisabled))}
                    </div>
                </div>
            );
        }

        const value = field.path ? getValueAtPath(field.path) : undefined;

        return (
            <div key={field.id} className={`bg-surface border border-border rounded-xl p-5 hover:border-primary/30 transition-all shadow-sm group ${disabled ? "opacity-60 grayscale" : ""}`}>
                <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                        <label className="text-sm font-semibold text-primary-text block mb-1">
                            {field.label}
                        </label>
                        {FIELD_DESCRIPTIONS[field.id] && (
                            <p className="text-xs text-secondary-text leading-relaxed mb-3">
                                {FIELD_DESCRIPTIONS[field.id]}
                            </p>
                        )}
                    </div>
                    {field.type === "toggle" && field.path && (
                        <Toggle
                            checked={!!value}
                            onChange={(v) => handleSettingChange(field.path!, v)}
                            disabled={disabled}
                        />
                    )}
                </div>

                {field.type !== "toggle" && (
                    <div className="mt-2">
                        {field.type === "dropdown" && field.path && (
                            <Dropdown
                                value={(value as string) || ""}
                                options={field.options || []}
                                onChange={(v) => handleSettingChange(field.path!, v)}
                                disabled={disabled}
                            />
                        )}
                        {field.type === "input" && field.path && (
                            <input
                                type={field.inputType === "number" ? "number" : "text"}
                                value={field.isArray ? ((value as string[]) || []).join(", ") : String(value ?? "")}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (field.isArray) {
                                        handleSettingChange(field.path!, val.split(",").map(s => s.trim()));
                                    } else if (field.inputType === "number") {
                                        handleSettingChange(field.path!, val === "" ? "" : Number(val));
                                    } else {
                                        handleSettingChange(field.path!, val);
                                    }
                                }}
                                disabled={disabled}
                                className="h-10 w-full bg-background border border-border rounded-lg px-4 text-sm text-primary-text shadow-sm hover:border-primary/20 focus:border-primary focus:ring-1 focus:ring-primary transition-colors font-mono outline-none"
                                placeholder={field.defaultValue ? String(field.defaultValue) : "Enter value..."}
                            />
                        )}
                        {field.type === "methodToggle" && field.path && (
                            <MethodToggle
                                value={(value as HttpMethod[]) || []}
                                options={field.options || []}
                                onChange={(v) => handleSettingChange(field.path!, v)}
                                disabled={disabled}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="w-full bg-surface/40 backdrop-blur-md border border-border/50 rounded-3xl overflow-hidden shadow-2xl flex flex-col lg:flex-row h-[700px]">
            {/* Left Side: Interactive Mockup */}
            <div className="flex-1 bg-background/50 py-4 lg:p-8 overflow-y-auto border-b lg:border-b-0 lg:border-r border-border/50 relative scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
               

                <div className="space-y-8">
                    <div className="flex items-center gap-4 mb-8 pb-6 border-b border-border/50">
                        <div className="p-3 bg-primary/10 rounded-xl shadow-inner">
                            {ICON_MAP[activeSection.icon] && React.createElement(ICON_MAP[activeSection.icon], { className: "w-8 h-8 text-primary" })}
                        </div>
                        <div>
                            <h3 className="text-2xl font-bold text-primary-text">{activeSection.name}</h3>
                            <p className="text-sm text-secondary-text mt-1">{SECTION_DESCRIPTIONS[activeSection.id]}</p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        {activeSection.fields.map((field) => renderField(field as FieldConfig))}
                    </div>
                </div>
            </div>

            {/* Right Side: Navigation */}
            <div className="w-full lg:w-1/3 bg-surface/60 p-6 lg:p-8 flex flex-col">
                <h3 className="text-2xl font-bold text-primary-text mb-2">Workspace Settings</h3>
                <p className="text-secondary-text mb-8">
                    Configure every aspect of your backend from a single intuitive dashboard.
                </p>

                <div className="flex-1 space-y-2 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
                    {SETTINGS_CONFIG.sections.map((section) => {
                        const Icon = ICON_MAP[section.icon] || Settings;
                        const isActive = activeSectionId === section.id;
                        return (
                            <button
                                key={section.id}
                                onClick={() => setActiveSectionId(section.id)}
                                className={`w-full text-left p-3 rounded-xl transition-all duration-300 flex items-start gap-3 group ${isActive
                                        ? "bg-primary text-white shadow-lg shadow-primary/20"
                                        : "hover:bg-primary/5 text-secondary-text hover:text-primary-text"
                                    }`}
                            >
                                <div className={`p-2 rounded-lg shrink-0 ${isActive ? "bg-white/20" : "bg-background border border-border group-hover:border-primary/30"
                                    }`}>
                                    <Icon className="w-4 h-4" />
                                </div>
                                <div>
                                    <div className="font-semibold text-sm mb-0.5">{section.name}</div>
                                    <div className={`text-xs leading-relaxed ${isActive ? "text-white/80" : "text-secondary-text/70"
                                        }`}>
                                        {isActive ? SECTION_DESCRIPTIONS[section.id] : "Click to view settings"}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
